import { useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { ActionIcon, Switch, Tooltip } from '@mantine/core'

import { Link } from '@redwoodjs/router'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_VOICE_AGENT } from 'src/graphql/voiceAgents.graphql'
import IconLink from 'src/icons/IconLink'

import SettingsField from '../../../components/SettingsField'
import SettingsSectionCard from '../../../components/SettingsSectionCard'

const ADVANCED_FLAGS = [
  {
    key: 'waitForGreeting',
    title: 'Wait For Greeting',
    description:
      'Wait for a greeting from the caller before starting the conversation.',
  },
  {
    key: 'sensitiveVoicemailDetection',
    title: 'Sensitive Voicemail Detection',
    description: 'Detect if the voice agent encounters a voicemail.',
  },
  {
    key: 'skipCallReason',
    title: 'Skip Call Reason',
    description: 'Skip explaining the reason for the call.',
  },
  {
    key: 'skipQualificationQuestions',
    title: 'Skip Qualification Questions',
    description: 'Skip asking qualification questions.',
  },
  {
    key: 'skipKnowledgeBase',
    title: 'Skip Knowledge Base',
    description: 'Skip using the knowledge base.',
  },
  {
    key: 'autoProgress',
    title: 'Auto Progress',
    description:
      'Automatically move the applicant forward based on the call outcome (e.g. booked appointment moves them to the application stage). Disqualified outcomes always apply.',
  },
] as const

/** Where a VoE agent's calling automation is configured. */
export const VOE_AUTOMATION_SETTINGS_PATH =
  '/settings?tab=company&section=employmentVerifications'

type AdvancedFlagKey = (typeof ADVANCED_FLAGS)[number]['key']
type AdvancedValues = Record<AdvancedFlagKey, boolean>

const getFlagValues = (voiceAgent): AdvancedValues => ({
  waitForGreeting: voiceAgent.waitForGreeting,
  sensitiveVoicemailDetection: voiceAgent.sensitiveVoicemailDetection,
  skipCallReason: voiceAgent.skipCallReason,
  skipQualificationQuestions: voiceAgent.skipQualificationQuestions,
  skipKnowledgeBase: voiceAgent.skipKnowledgeBase,
  autoProgress: voiceAgent.autoProgress,
})

const AdvancedCard = ({ voiceAgent, refetchVoiceAgents }) => {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<AdvancedValues>(
    getFlagValues(voiceAgent)
  )

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues(getFlagValues(voiceAgent))
    }
  }, [voiceAgent, editing])

  const [updateVoiceAgent, { loading }] = useMutation(UPDATE_VOICE_AGENT, {
    onCompleted: () => {
      toast('Voice agent updated successfully', 'success')
      setEditing(false)
      refetchVoiceAgents()
    },
    onError: (error) => {
      toast(`Failed to update voice agent: ${error.message}`, 'error')
    },
  })

  const handleSave = () => {
    updateVoiceAgent({
      variables: {
        voiceAgentId: voiceAgent.voiceAgentId,
        input: { ...values },
      },
    })
  }

  const handleCancel = () => {
    setValues(getFlagValues(voiceAgent))
    setEditing(false)
  }

  return (
    <SettingsSectionCard
      title={
        <div className="flex flex-row items-center gap-2">
          Advanced
          <Tooltip label="Admin only">
            <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
          </Tooltip>
        </div>
      }
      initialOpened={false}
      editable
      editing={editing}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
      loading={loading}
    >
      {ADVANCED_FLAGS.map(({ key, title, description }) => (
        <SettingsField key={key} title={title} description={description}>
          <Switch
            checked={values[key]}
            disabled={!editing || loading}
            onChange={(e) =>
              setValues({ ...values, [key]: e.currentTarget.checked })
            }
          />
        </SettingsField>
      ))}
      {voiceAgent.type === 'VOE' && (
        <SettingsField
          title="Automation Rules"
          description="When this agent calls employers automatically. Configured under Company → Employment Verifications."
        >
          <Tooltip label="Open automation rules">
            <ActionIcon
              component={Link}
              to={VOE_AUTOMATION_SETTINGS_PATH}
              variant="subtle"
              color="gray"
              aria-label="Open automation rules"
            >
              <IconLink className="h-4 w-4 fill-none stroke-doubleNickel-gray-600" />
            </ActionIcon>
          </Tooltip>
        </SettingsField>
      )}
    </SettingsSectionCard>
  )
}

export default AdvancedCard
